import { getChineseFt, getLanguage, getTranslation } from "@/common/translate";

const containsChinese = (text: string) => /[\u3400-\u9fff]/.test(text);

const termMap: Record<string, string> = {
  测点: "point",
  设备: "equipment",
  设备3D图: "equipment 3D image",
  设备开关机配置: "equipment power on/off configuration",
  智能诊断报警: "intelligent diagnosis alarm",
  智能诊断: "intelligent diagnosis",
  采集单元监测: "edge gateway monitoring",
  硬件监测: "hardware monitoring",
  传感器: "sensor",
  工厂: "factory",
  工厂信息: "factory information",
  知识库: "knowledge base",
  用户: "user",
  账号: "account",
  安全策略: "security policy",
  策略: "policy",
  基础: "basic",
  管理: "management",
  操作: "operation",
  菜单: "menu",
  角色: "role",
  状态: "status",
  工单: "work order",
  配置: "configuration",
  信息: "information",
  报警: "alarm",
  模型: "model",
  模型配置: "model configuration",
  参数: "parameter",
  日志: "log",
};

const phraseMap: Record<string, string> = {
  成功: "successfully",
  失败: "failed",
  创建: "Created",
  更新: "Updated",
  删除: "Deleted",
  添加: "Added",
  修改: "Updated",
  启用: "Enabled",
  禁用: "Disabled",
  访问: "Access",
  登录: "Log in",
  退出: "Log out",
  或密码错误: "or password is incorrect",
  账号或密码错误: "Account or password is incorrect",
  管理员: "Administrator",
};

const translateTerm = (value: string): string => {
  if (!value) return "";

  if (termMap[value]) return termMap[value];

  const fromDict = getLanguage(value, "English");
  if (fromDict && fromDict !== value) return fromDict;

  const fromApi = getTranslation(value, "English");
  if (fromApi && fromApi !== value) return fromApi;

  return value;
};

const normalizeEnglishLogText = (value: string): string => {
  if (!value) return "";

  let text = value;

  // Normalize full-width Chinese punctuation used in some log payloads.
  text = text
    .replace(/，/g, ",")
    .replace(/：/g, ":")
    .replace(/；/g, ";")
    .replace(/（/g, "(")
    .replace(/）/g, ")");

  // Common glued words seen in backend log payloads.
  text = text
    .replace(/modelconfiguration/gi, "model configuration")
    .replace(/configuration-account/gi, "configuration account")
    .replace(/\baccount\s+or\s+password\s+is\s+incorrect\b/gi, "Account or password is incorrect");

  // Insert missing spaces around common action verbs and adverbs.
  text = text
    .replace(/(Created|Updated|Deleted|Enabled|Disabled)(?=[A-Za-z0-9])/g, "$1 ")
    .replace(/(?<=[A-Za-z0-9])(successfully|failed)(?=[A-Za-z0-9!]|$)/gi, " $1 ");

  // Split camel-cased or glued English boundaries.
  text = text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])([0-9])/g, "$1 $2")
    .replace(/([0-9])([A-Za-z])/g, "$1 $2");

  // Final whitespace normalization.
  text = text
    .replace(/\s*,\s*/g, ", ")
    .replace(/\s*:\s*/g, ": ")
    .replace(/\s*;\s*/g, "; ")
    .replace(/\s+/g, " ")
    .replace(/\s+([!.,:;])/g, "$1")
    .trim();

  return text;
};

const translateChineseSegments = (value: string): string => {
  let translated = value;

  // Replace longer terms first to avoid partial matches.
  const sortedTerms = Object.keys(termMap).sort((a, b) => b.length - a.length);
  for (const term of sortedTerms) {
    translated = translated.split(term).join(` ${termMap[term]} `);
  }

  translated = translated.replace(/[\u3400-\u9fff]+/g, (segment) => {
    const dictResult = getLanguage(segment, "English");
    if (dictResult && dictResult !== segment) return dictResult;

    const apiResult = getTranslation(segment, "English");
    if (apiResult && apiResult !== segment) return apiResult;

    return segment;
  });

  return normalizeEnglishLogText(translated);
};

const heuristicTranslateLogText = (rawText: string): string => {
  // Mixed payload: Updated factory information successfully
  const mixedActionMatch = rawText.match(/^(Created|Updated|Deleted|Enabled|Disabled)\s*(.+?)\s*successfully!?$/i);
  if (mixedActionMatch) {
    const action = mixedActionMatch[1][0].toUpperCase() + mixedActionMatch[1].slice(1).toLowerCase();
    const target = translateChineseSegments(mixedActionMatch[2]);
    return normalizeEnglishLogText(`${action} ${target} successfully`);
  }

  // Mixed payload: Updated model configuration successfully / Updated model configuration successfully
  const modifyMatch = rawText.match(/^修改\s*(.+?)\s*(成功|successfully)?!?$/i);
  if (modifyMatch) {
    const target = translateChineseSegments(modifyMatch[1]);
    return normalizeEnglishLogText(`Updated ${target} successfully`);
  }

  const createMatch = rawText.match(/^创建(.+)成功$/);
  if (createMatch) {
    return normalizeEnglishLogText(`Created ${translateTerm(createMatch[1])} successfully`);
  }

  const updateMatch = rawText.match(/^更新(.+)成功$/);
  if (updateMatch) {
    return normalizeEnglishLogText(`Updated ${translateTerm(updateMatch[1])} successfully`);
  }

  const deleteMatch = rawText.match(/^删除(.+)成功$/);
  if (deleteMatch) {
    return normalizeEnglishLogText(`Deleted ${translateTerm(deleteMatch[1])} successfully`);
  }

  const enableMatch = rawText.match(/^启用(.+)成功$/);
  if (enableMatch) {
    return normalizeEnglishLogText(`Enabled ${translateTerm(enableMatch[1])} successfully`);
  }

  const disableMatch = rawText.match(/^禁用(.+)成功$/);
  if (disableMatch) {
    return normalizeEnglishLogText(`Disabled ${translateTerm(disableMatch[1])} successfully`);
  }

  let translated = rawText;
  for (const [cn, en] of Object.entries(phraseMap)) {
    translated = translated.split(cn).join(en);
  }

  translated = translateChineseSegments(translated);

  return normalizeEnglishLogText(translated);
};

export const getLogDisplayText = (
  record: any,
  field: string,
  text: any,
  language: string
): string => {
  const rawText = (text ?? "").toString();
  if (!rawText) return "";

  if (language === "Chinese_ft") {
    return getChineseFt(rawText);
  }

  if (language !== "English") {
    return rawText;
  }

  const englishField = `${field}En`;
  if (record && record[englishField]) {
    return normalizeEnglishLogText(record[englishField]);
  }

  const fromDict = getLanguage(rawText, "English");
  if (fromDict && fromDict !== rawText) {
    return normalizeEnglishLogText(fromDict);
  }

  const fromApi = getTranslation(rawText, "English");
  if (fromApi && fromApi !== rawText) {
    return normalizeEnglishLogText(fromApi);
  }

  if (containsChinese(rawText)) {
    return heuristicTranslateLogText(rawText);
  }

  return normalizeEnglishLogText(rawText);
};

export const collectUntranslatedChinesePhrases = (
  records: any[],
  fields: string[],
  language: string
): string[] => {
  if (language !== "English" || !Array.isArray(records) || !records.length) {
    return [];
  }

  const unknown = new Set<string>();

  records.forEach((record: any) => {
    fields.forEach((field) => {
      const rawValue = (record?.[field] ?? "").toString();
      if (!rawValue) return;

      const translated = getLogDisplayText(record, field, rawValue, language);
      const segments = translated.match(/[\u3400-\u9fff]+/g) || [];
      segments.forEach((seg) => {
        if (seg && seg.trim()) unknown.add(seg.trim());
      });
    });
  });

  return [...unknown];
};
